'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Ticket, Mail, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InlineSpinner } from '@/components/ui/spinner';
import { EMAIL_REGEX } from '@/components/registration/special/registry';
import { useMicrositeThemeVars } from '@/hooks/use-microsite-theme-vars';
import { claimBibMarketOffer } from '@/services/bibmarket';
import type { BibMarketOffer, BibMarketTranslations } from '@/types/bibmarket';
import { formatPrice } from '@/utils/locale';

interface BibMarketClientProps {
  offers: BibMarketOffer[];
  eventId: number;
  eventTitle: string;
  lang: string;
  basePath: string;
  currency: string;
  t: BibMarketTranslations;
}

function optionChipLabel(o: { questionName?: string; label: string; quantity?: number }): string {
  // Antepone el nombre de la pregunta ("Talla camiseta: L") — solo la opción no siempre se entiende.
  const base = o.questionName ? `${o.questionName}: ${o.label}` : o.label;
  return o.quantity && o.quantity > 1 ? `${base} (×${o.quantity})` : base;
}

// "hace 2 horas" / "hace 3 días" sobre el publishedAt ISO del doc. Fallback a cadena vacía
// si la fecha no parsea (docs legacy o campo ausente) — la meta simplemente omite la parte.
function relativeTime(iso: string | undefined, lang: string): string {
  if (!iso) return '';
  const ts = Date.parse(/Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(ts)) return '';
  const diffMs = ts - Date.now();
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const minutes = Math.round(diffMs / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(diffMs / 3600000);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  const days = Math.round(diffMs / 86400000);
  if (Math.abs(days) < 30) return rtf.format(days, 'day');
  return rtf.format(Math.round(days / 30), 'month');
}

function offerRef(o: BibMarketOffer): string {
  return `BLS-${String(o.changeOwnerId).padStart(4, '0')}`;
}

export default function BibMarketClient({ offers: offersProp, eventId, eventTitle, lang, basePath, currency, t }: BibMarketClientProps) {
  // Listado en estado local: al reclamar OK (o si alguien ganó la carrera) la oferta desaparece.
  const [offers, setOffers] = useState<BibMarketOffer[]>(offersProp);
  const [modalOffer, setModalOffer] = useState<BibMarketOffer | null>(null);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  // Email con el que se reservó (para el cuerpo del éxito tras quitar la oferta del listado).
  const [sentEmail, setSentEmail] = useState('');

  // El Dialog monta en un portal fuera del <div data-ms-theme>, perdiendo el color del evento
  // (ms-primary cae al verde por defecto). Reinyectamos las vars del tema en el DialogContent.
  const themeVars = useMicrositeThemeVars();

  const emailValid = EMAIL_REGEX.test(email.trim());

  const closeModal = () => {
    setModalOffer(null);
    setEmail('');
    setSent(false);
    setModalError('');
  };

  const submit = async () => {
    if (!emailValid || !modalOffer || submitting) return;
    setSubmitting(true);
    setModalError('');
    const id = modalOffer.changeOwnerId;
    try {
      const res = await claimBibMarketOffer(lang, eventId, id, email.trim());
      if (res.ok) {
        setSentEmail(email.trim());
        setOffers(prev => prev.filter(o => o.changeOwnerId !== id)); // desaparece del listado
        setSent(true);
      } else if (res.errorId === 'same_as_holder') {
        setModalError(t.modalErrorSameHolder);
      } else if (res.errorId === 'already_claimed') {
        // Otro reclamante ganó la carrera: lo quitamos del listado y avisamos.
        setOffers(prev => prev.filter(o => o.changeOwnerId !== id));
        setModalError(t.modalErrorAlreadyClaimed);
      } else {
        setModalError(t.modalErrorGeneric);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
        <div>
          {/* Head */}
          <div className="mb-6">
            <p className="text-[11px] font-bold uppercase tracking-widest text-ms-primary mb-1">{t.eyebrow}</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-ms-text dark:text-white mb-2">
              {t.title}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-300 max-w-2xl">{t.desc}</p>
          </div>

          {offers.length === 0 ? (
            /* Empty state */
            <div className="rounded-ms border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-10 flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-ms-primary/10 flex items-center justify-center">
                <Ticket className="w-7 h-7 text-ms-primary" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-ms-text dark:text-white">{t.emptyTitle}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-300 max-w-md">{t.emptyDesc}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {offers.map(o => {
                return (
                  <article
                    key={o.changeOwnerId}
                    className="rounded-ms border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-ms-text dark:text-white">
                        {o.feeName ?? offerRef(o)}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <span>{t.refLabel.replace('%id%', offerRef(o))}</span>
                        {relativeTime(o.publishedAt, lang) && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{t.published.replace('%time%', relativeTime(o.publishedAt, lang))}</span>
                          </>
                        )}
                      </div>
                      {(o.includedOptions?.length ?? 0) > 0 && (
                        <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            {t.includedOptions}
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {o.includedOptions!.map((opt, idx) => (
                              <span key={idx} className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                                {optionChipLabel(opt)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {o.message && (
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            {t.observations}
                          </p>
                          <p className="text-xs italic text-gray-600 dark:text-gray-300 mt-0.5 break-words">
                            “{o.message}”
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex items-center gap-4 sm:flex-col sm:items-end sm:gap-3">
                      {o.price != null && (
                        <span className="text-2xl font-black text-ms-text dark:text-white whitespace-nowrap">
                          {formatPrice(o.price, currency)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setModalOffer(o)}
                        className="inline-flex items-center justify-center h-10 px-5 rounded-ms-md bg-ms-primary text-ms-on-primary text-sm font-semibold hover:bg-ms-primary-hover transition-colors"
                      >
                        {t.request}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {/* Side rail */}
        <aside className="flex flex-col gap-4">
          <div className="rounded-ms border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
            <h4 className="text-sm font-bold text-ms-text dark:text-white mb-4">{t.howTitle}</h4>
            <div className="flex flex-col gap-3.5">
              {([
                [t.how1Strong, t.how1Rest],
                [t.how2Strong, t.how2Rest],
                [t.how3Strong, t.how3Rest],
              ] as const).map(([strong, rest], idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-ms-primary/10 text-ms-primary text-xs font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                    <b className="text-ms-text dark:text-white">{strong}</b> {rest}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-ms border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
            <h4 className="text-sm font-bold text-ms-text dark:text-white mb-2">{t.offerTitle}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              {t.offerDesc.replace('%event%', eventTitle)}
            </p>
            <Link
              href={`${basePath}/my-registration`}
              className="w-full inline-flex items-center justify-center h-10 px-4 rounded-ms-md border border-ms-primary text-ms-primary text-sm font-semibold hover:bg-ms-primary/5 transition-colors"
            >
              {t.offerCta}
            </Link>
          </div>
        </aside>
      </div>

      {/* Modal "Solicitar dorsal" — stub visual: recap + email + success local. */}
      <Dialog open={modalOffer !== null} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent style={themeVars}>
          {!sent ? (
            <>
              <DialogHeader>
                <DialogTitle>{t.modalTitle}</DialogTitle>
              </DialogHeader>
              {modalOffer && (
                <div className="rounded-ms-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 text-sm">
                  <div className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-gray-500 dark:text-gray-400">{t.modalFee}</span>
                    <span className="font-semibold text-ms-text dark:text-white text-right">{modalOffer.feeName ?? '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-gray-500 dark:text-gray-400">{t.modalRef}</span>
                    <span className="font-semibold text-ms-text dark:text-white">{offerRef(modalOffer)}</span>
                  </div>
                  {(modalOffer.includedOptions?.length ?? 0) > 0 && (
                    <div className="flex justify-between gap-4 px-3 py-2">
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">{t.modalOptions}</span>
                      <span className="text-ms-text dark:text-white text-right">
                        {modalOffer.includedOptions!.map(optionChipLabel).join(' · ')}
                      </span>
                    </div>
                  )}
                  {modalOffer.message && (
                    <div className="flex justify-between gap-4 px-3 py-2">
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">{t.modalObs}</span>
                      <span className="italic text-ms-text dark:text-white text-right break-words">“{modalOffer.message}”</span>
                    </div>
                  )}
                  {modalOffer.price != null && (
                    <div className="flex justify-between gap-4 px-3 py-2">
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">{t.modalPrice}</span>
                      <span className="font-bold text-ms-primary text-right">{formatPrice(modalOffer.price, currency)}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="bibmarket-email" className="text-xs font-semibold text-foreground">
                  {t.modalEmail}
                </label>
                <input
                  id="bibmarket-email"
                  type="email"
                  autoComplete="email"
                  placeholder={t.modalEmailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  className="w-full rounded-ms-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div className="flex items-start gap-2.5 rounded-ms-md bg-ms-primary/5 border border-ms-primary/20 px-3 py-2.5">
                <Mail className="w-4 h-4 text-ms-primary shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{t.modalNote}</p>
              </div>
              {modalError && (
                <div className="flex items-start gap-2 rounded-ms-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2.5">
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">{modalError}</p>
                </div>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={!emailValid || submitting}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-ms-md bg-ms-primary text-ms-on-primary text-sm font-semibold hover:bg-ms-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <InlineSpinner size="sm" />}
                {t.request}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center text-center gap-3 py-2">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-ms-text dark:text-white">{t.successTitle}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {modalOffer && t.successBody
                  .replace('%email%', sentEmail)
                  .replace('%ref%', offerRef(modalOffer))}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{t.successFine}</p>
              <button
                type="button"
                onClick={closeModal}
                className="mt-1 inline-flex items-center justify-center h-10 px-6 rounded-ms-md bg-ms-primary text-ms-on-primary text-sm font-semibold hover:bg-ms-primary-hover transition-colors"
              >
                {t.successOk}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
